import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { departmentApi } from '../../api/department.api';
import { userApi } from '../../api/user.api';
import { useAuth } from '../../store/AuthContext';
import { Avatar, Badge, EmptyState, PageHeader, Skeleton, Btn, Select, Input } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { useDebounce } from '../../hooks/useDraft';
import { useUI } from '../../store/UIContext';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { MagnifyingGlass } from '@phosphor-icons/react';

const COLORS = ['#29AAE1', '#8b5cf6', '#059669', '#f59e0b', '#ef4444', '#f97316', '#0ea5e9', '#6366f1'];

export default function DepartmentListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useUI();
  const canChangeManager = user?.type === 'dispatcher' || user?.type === 'admin';

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editDept, setEditDept] = useState(null);
  const [renameDept, setRenameDept] = useState(null);
  const [deptName, setDeptName] = useState('');
  const [deptDisplayName, setDeptDisplayName] = useState('');
  const [deptDescription, setDeptDescription] = useState('');
  const [deptAiDescription, setDeptAiDescription] = useState('');
  const [deptAiKeywords, setDeptAiKeywords] = useState('');
  const [deleteDept, setDeleteDept] = useState(null);
  const [managerUsername, setManagerUsername] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingRename, setSavingRename] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const q = useDebounce(search, 300);

  const loadDepartments = () => departmentApi.getDepartmentListView()
    .then((r) => setDepartments(Array.isArray(r.data) ? r.data : []))
    .catch(() => addToast('خطا در بارگذاری دپارتمان‌ها', 'error'));

  useEffect(() => {
    loadDepartments().finally(() => setLoading(false));
    if (canChangeManager) {
      userApi.getEmployeeListView()
        .then((r) => setEmployees(Array.isArray(r.data) ? r.data.filter((e) => e.active) : []))
        .catch(() => {});
    }
  }, [canChangeManager]);

  useAutoRefresh(loadDepartments, { paused: !!editDept || !!deleteDept || !!renameDept });

  const isProtectedDept = (name) => {
    const n = (name || '').trim().toLowerCase();
    return n === 'dispatcher' || (name || '').trim() === 'دستیار هوش مصنوعی';
  };

  const filtered = departments.filter((d) => !q || (d.name || '').includes(q) || (d.display_name || '').includes(q));

  const openEditManager = (dept) => {
    setEditDept(dept);
    setManagerUsername(dept.manager || '');
  };

  const openRename = async (dept) => {
    setRenameDept(dept);
    setDeptName(dept.name || '');
    setDeptDisplayName(dept.display_name && dept.display_name !== dept.name ? dept.display_name : '');
    setDeptDescription('');
    setDeptAiDescription('');
    setDeptAiKeywords('');
    try {
      const res = await departmentApi.getDepartmentInfo(dept.id);
      setDeptDescription(res.data?.description || '');
      setDeptAiDescription(res.data?.ai_description || '');
      setDeptAiKeywords(res.data?.ai_keywords || '');
    } catch {
      addToast('خطا در دریافت اطلاعات دپارتمان', 'error');
    }
  };

  const saveRename = async () => {
    if (!deptName.trim()) {
      addToast('نام دپارتمان الزامی است', 'error');
      return;
    }
    setSavingRename(true);
    try {
      await departmentApi.editDepartmentInfo({
        id: renameDept.id,
        name: deptName.trim(),
        display_name: deptDisplayName.trim(),
        description: deptDescription,
        ai_description: deptAiDescription,
        ai_keywords: deptAiKeywords,
      });
      addToast('دپارتمان به‌روزرسانی شد', 'success');
      setRenameDept(null);
      await loadDepartments();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در ویرایش دپارتمان', 'error');
    }
    setSavingRename(false);
  };

  const saveManager = async () => {
    if (!managerUsername) {
      addToast('مدیر دپارتمان را انتخاب کنید', 'error');
      return;
    }
    setSaving(true);
    try {
      await departmentApi.setDepartmentManager({
        department_id: editDept.id,
        manager_name: managerUsername,
      });
      addToast('مدیر دپارتمان تغییر کرد', 'success');
      setEditDept(null);
      await loadDepartments();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در تغییر مدیر', 'error');
    }
    setSaving(false);
  };

  const confirmDelete = async () => {
    if (!deleteDept?.id) return;
    setDeleting(true);
    try {
      await departmentApi.deleteDepartment({ department_id: deleteDept.id });
      addToast('دپارتمان حذف شد', 'success');
      setDeleteDept(null);
      await loadDepartments();
    } catch (err) {
      const msg = err.response?.data;
      addToast(typeof msg === 'string' && msg ? msg : 'خطا در حذف دپارتمان', 'error');
    }
    setDeleting(false);
  };

  const employeeOptions = [
    { value: '', label: '— انتخاب مدیر —' },
    ...employees.map((e) => ({
      value: e.username,
      label: `${e.username}${e.first_name || e.last_name ? ` (${[e.first_name, e.last_name].filter(Boolean).join(' ')})` : ''}${e.department_name ? ` — ${e.department_name}` : ''}${e.is_manager ? ' ★مدیر' : ''}`,
    })),
  ];

  return (
    <div className="fade-in">
      <PageHeader
        title="لیست دپارتمان‌ها"
        subtitle={`${toPersian(filtered.length)} دپارتمان`}
        action={canChangeManager ? <Btn onClick={() => navigate('/departments/add')}>+ افزودن دپارتمان</Btn> : null}
      />

      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="جستجو..."
          style={{
            marginRight: 'auto', padding: '7px 14px', border: '1.5px solid #e2e8f0',
            borderRadius: 20, fontSize: 12, outline: 'none', fontFamily: 'inherit', width: 200,
          }}
        />
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
          {[1, 2, 3, 4].map((k) => (
            <div key={k} style={{ background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' }}>
              <Skeleton h={20} style={{ marginBottom: 10 }} /><Skeleton h={14} w="70%" />
            </div>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<MagnifyingGlass size={44} weight="regular" />} title="دپارتمانی یافت نشد" />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(260px,1fr))', gap: 14 }}>
          {filtered.map((d, i) => (
            <div
              key={d.id || i}
              className="pj-card-hover"
              style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 20 }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: canChangeManager ? 12 : 0 }}>
                <Avatar name={(d.name || '').slice(0, 2)} size={44} color={COLORS[i % COLORS.length]} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.display_name || d.name}
                  </div>
                  {d.display_name && d.display_name !== d.name && <div style={{ fontSize: 11, color: '#94a3b8' }} dir="auto">نام اصلی: {d.name}</div>}
                  {d.manager
                    ? <div style={{ fontSize: 11, color: '#94a3b8' }}>مدیر: {d.manager}</div>
                    : <Badge variant="gray" style={{ marginTop: 4 }}>بدون مدیر</Badge>}
                </div>
              </div>
              {canChangeManager && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {!isProtectedDept(d.name) && (
                    <Btn size="sm" variant="primary" onClick={() => openRename(d)} style={{ width: '100%', justifyContent: 'center' }}>
                      ویرایش نام / توضیحات
                    </Btn>
                  )}
                  <Btn size="sm" variant="secondary" onClick={() => openEditManager(d)} style={{ width: '100%', justifyContent: 'center' }}>
                    تغییر مدیر دپارتمان
                  </Btn>
                  {!isProtectedDept(d.name) && (
                    <Btn size="sm" variant="danger" onClick={() => setDeleteDept(d)} style={{ width: '100%', justifyContent: 'center' }}>
                      حذف دپارتمان
                    </Btn>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {deleteDept && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 8px', color: '#0f172a' }}>
              حذف دپارتمان «{deleteDept.name}»؟
            </h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
              فقط وقتی امکان‌پذیر است که کارمندی در این دپارتمان نباشد و تیکتی به آن ارجاع نشده باشد.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn variant="secondary" onClick={() => setDeleteDept(null)} disabled={deleting}>انصراف</Btn>
              <Btn variant="danger" onClick={confirmDelete} loading={deleting}>حذف</Btn>
            </div>
          </div>
        </div>
      )}

      {renameDept && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 16px', color: '#0f172a' }}>
              ویرایش دپارتمان
            </h3>
            <Input label="نام دپارتمان *" value={deptName} onChange={(e) => setDeptName(e.target.value)} />
            <Input label="نام نمایشی" placeholder="نامی که به مسئولان نمایش داده می‌شود (اختیاری)" value={deptDisplayName} onChange={(e) => setDeptDisplayName(e.target.value)} wrapStyle={{ marginTop: 12 }} />
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>توضیحات</label>
              <textarea
                value={deptDescription}
                onChange={(e) => setDeptDescription(e.target.value)}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
                توضیحات برای هوش مصنوعی
              </label>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                این متن فقط برای ارجاع خودکار تیکت توسط هوش‌مصنوعی استفاده می‌شود؛ هر کلمه/موضوعی که این دپارتمان باید مسئول آن باشد را اینجا بنویسید.
              </div>
              <textarea
                value={deptAiDescription}
                onChange={(e) => setDeptAiDescription(e.target.value)}
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#334155', display: 'block', marginBottom: 6 }}>
                کلیدواژه‌ها برای هوش مصنوعی
              </label>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>
                کلمات دقیقی که مشتری‌ها معمولاً برای این دپارتمان به‌کار می‌برند، با کاما جدا کنید (مثال: کابل, قطعی, پرینتر).
              </div>
              <textarea
                value={deptAiKeywords}
                onChange={(e) => setDeptAiKeywords(e.target.value)}
                rows={2}
                placeholder="کابل, قطعی, پرینتر, اینترنت"
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 14px', border: '1.5px solid #e2e8f0',
                  borderRadius: 8, fontSize: 13, fontFamily: 'inherit', resize: 'vertical',
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setRenameDept(null)} disabled={savingRename}>انصراف</Btn>
              <Btn onClick={saveRename} loading={savingRename}>ذخیره</Btn>
            </div>
          </div>
        </div>
      )}

      {editDept && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
        }}
        >
          <div style={{
            background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 420,
            maxHeight: '90vh', overflowY: 'auto',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
          >
            <h3 style={{ fontSize: 15, fontWeight: 700, margin: '0 0 6px', color: '#0f172a' }}>
              تغییر مدیر — {editDept.name}
            </h3>
            <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.7 }}>
              کارمند انتخاب‌شده مدیر این دپارتمان می‌شود و به این دپارتمان منتقل می‌شود.
              {editDept.manager && <> مدیر فعلی: <strong>{editDept.manager}</strong></>}
            </p>
            <Select
              label="مدیر جدید *"
              value={managerUsername}
              onChange={(e) => setManagerUsername(e.target.value)}
              options={employeeOptions}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <Btn variant="secondary" onClick={() => setEditDept(null)} disabled={saving}>انصراف</Btn>
              <Btn onClick={saveManager} loading={saving}>ذخیره</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
